import {
  Button,
  CircularProgress,
  createStyles,
  FormControl,
  FormLabel,
  Grid,
  Link,
  makeStyles,
  TextField,
  Theme,
  Typography,
} from '@material-ui/core';
import { MUIDataTableColumn, MUIDataTableMeta } from 'mui-datatables';
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import cautionEnter from '../../../../assets/images/caution-enter.png';
import ConfirmationDialog from '../../../../components/ConfirmationDialog';
import CustomDataTable from '../../../../components/CustomDataTable';
import CustomDialog from '../../../../components/CustomDialog';
import ImagePreviewDialog, {
  ImageData,
} from '../../../../components/ImagePreviewDialog';
import ToogleButtonMenu from '../../../../components/ToogleButtonMenu';
import { useLoadingDialog } from '../../../../hooks/backdrop';
import { useFeedback } from '../../../../hooks/feedback';
import { PreRegisterDTO } from '../../../../models/dto/PreRegisterDTO';
import { getEnum } from '../../../../models/enum/BaseEnum';
import {
  APROVADO,
  PENDENTE,
  PREREGISTRATION_STATUS_ENUM,
  RECUSADO,
} from '../../../../models/enum/PreRegistratioStatusEnum';
import { getEmployeeImgByHash } from '../../../../services/peopleManagement.service';
import {
  approveOrReproveEmployeeRegister,
  deleteEmployeeFaceRecognition,
  findAllAppPreRegistrations,
  findAllEmployeeFromFaceRecognition,
} from '../../../../services/personnelDepartment.service';
import { AppState } from '../../../../store';
import { CondominiumReducerData } from '../../../../store/reducers/condominiumReducer';
import { formatCPF } from '../../../../utils/formatCPF';
import { isEmpty } from '../../../../utils/validation.util';
import InformativeCard from '../InformativeCard';

export const useStyles = makeStyles((_theme: Theme) =>
  createStyles({
    centerContent: {
      '& div': {
        display: 'flex',
        justifyContent: 'center',
      },
    },
    toggleButtons: {
      backgroundColor: 'transparent',
    },
  }),
);

type RegisterActionType = 'Aprovar' | 'Reprovar' | 'Excluir' | '';

const EmployeesRegistersCard: React.FC = () => {
  const classes = useStyles();
  const [openDialog, setOpenDialog] = useState(false);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingImage, setLoadingImage] = useState(false);
  const [registers, setRegisters] = useState<PreRegisterDTO[]>([]);
  const [registerSelected, setRegisterSelected] = useState<PreRegisterDTO>();
  const [imagePreviewDialogTitle, setImagePreviewDialogTitle] = useState('');
  const [reason, setReason] = useState<string>('');
  const [actionType, setActionType] = useState<RegisterActionType>('');
  const { handleLoadingDialog } = useLoadingDialog();
  const [openImagePreviewDialog, setOpenImagePreviewDialog] = useState<boolean>(
    false,
  );
  const [logDistanceImageSelected, setLogDistanceImageSelected] = useState<
    ImageData | undefined
  >();
  const { addFeedback } = useFeedback();
  const { condominiumSelected }: CondominiumReducerData = useSelector(
    (state: AppState) => state.condominiumReducer,
  );

  const getLogDistanceImage = useCallback(
    async (rokognitHash: string) => {
      try {
        setLoadingImage(true);

        const data = await getEmployeeImgByHash(rokognitHash);
        setLogDistanceImageSelected({
          contentType: 'jpg',
          fileBuffer: data.data,
        });
      } catch (error) {
        addFeedback({
          type: 'error',
          description: 'Ocorreu um erro ao buscar a imagem.',
        });
      } finally {
        setLoadingImage(false);
      }
    },
    [addFeedback],
  );

  const handleOpenImagePreviewDialog = (
    employeeName: string,
    date: string,
    rokognitHash: string,
  ) => {
    getLogDistanceImage(rokognitHash);
    setImagePreviewDialogTitle(`${date} - ${employeeName}`);
    setOpenImagePreviewDialog(true);
  };

  const handleCardClick = useCallback(async () => {
    try {
      if (condominiumSelected?.referencia) {
        setRegisters([]);
        setOpenDialog(true);
        setLoading(true);
        const { data } = await findAllAppPreRegistrations(
          condominiumSelected?.referencia,
        );
        const {
          data: faceRekoginition,
        } = await findAllEmployeeFromFaceRecognition(
          condominiumSelected?.referencia,
        );

        let formattedData: PreRegisterDTO[] =
          faceRekoginition?.map(item => {
            return {
              pessoa: item,
              numCad: item.numeroCadastro,
              hashArquivo: item.rekognit?.hash,
            } as PreRegisterDTO;
          }) ?? [];

        formattedData = [
          ...formattedData,
          ...data?.filter(item => item.status !== '3'),
        ];
        setRegisters(formattedData);
      }
    } catch (error) {
      addFeedback({
        type: 'error',
        description: 'Ocorreu um erro ao buscar as informações.',
      });
    } finally {
      setLoading(false);
    }
  }, [addFeedback, condominiumSelected]);

  const toggleActionsMenu = (fileHash: string) => {
    setRegisterSelected(registers.find(item => item.hashArquivo === fileHash));
  };

  const handleOpenConfirmationDialog = (action: RegisterActionType) => {
    setActionType(action);
    setOpenConfirmationDialog(true);
  };

  const handleApproveReproveRegister = useCallback(
    async (fileHash: string) => {
      const isApproval = actionType === 'Aprovar';
      try {
        await approveOrReproveEmployeeRegister(
          fileHash,
          isApproval
            ? String(APROVADO.numberValue)
            : String(RECUSADO.numberValue),
          !isApproval ? reason : undefined,
        );
        addFeedback({
          type: isApproval ? 'success' : 'warning',
          description: `Pré-cadastro ${
            isApproval ? 'aprovado' : 'reprovado'
          } com sucesso!`,
        });

        setOpenImagePreviewDialog(false);
      } catch (error) {
        console.log(error);
        addFeedback({
          type: 'error',
          description: `Ocorreu um erro ao ${
            isApproval ? 'aprovar' : 'reprovar'
          } o pré-cadastro!`,
        });
      } finally {
        handleLoadingDialog(false);
      }
    },
    [actionType, addFeedback, handleLoadingDialog, reason],
  );

  const removeRegister = useCallback(
    async (numeroCracha: number, registrationNumber: number) => {
      try {
        await deleteEmployeeFaceRecognition(numeroCracha, registrationNumber);
        addFeedback({
          type: 'success',
          description: `Pré-cadastro removido com sucesso!`,
        });

        setOpenImagePreviewDialog(false);
      } catch (error) {
        console.log(error);
        addFeedback({
          type: 'error',
          description: `Ocorreu um erro ao excluir o pré-cadastro!`,
        });
      } finally {
        handleLoadingDialog(false);
      }
    },
    [addFeedback, handleLoadingDialog],
  );

  const handleDialogConfirmationButton = useCallback(async () => {
    handleLoadingDialog(true);
    setOpenConfirmationDialog(false);
    if (registerSelected) {
      if (actionType === 'Aprovar' || actionType === 'Reprovar') {
        await handleApproveReproveRegister(registerSelected.hashArquivo);
      } else if (actionType === 'Excluir') {
        await removeRegister(
          registerSelected.pessoa.numeroCadastro,
          registerSelected.pessoa.numeroCracha,
        );
      }
      handleCardClick();
    }
  }, [
    actionType,
    handleApproveReproveRegister,
    handleCardClick,
    handleLoadingDialog,
    registerSelected,
    removeRegister,
  ]);

  const columns: MUIDataTableColumn[] = [
    {
      name: 'pessoa.cpf',
      label: 'CPF',
      options: {
        customBodyRender: (value: string, _tableMeta: any, _updateValue: any) =>
          formatCPF(value),
      },
    },
    { name: 'criadoEm', label: 'Data de Cadastro' },
    { name: 'numCad', label: 'Número de Cadastro' },
    {
      name: 'pessoa.nome',
      label: 'Nome',
    },
    {
      name: 'hashArquivo',
      label: 'Foto de Cadastro',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (
          value: string,
          tableMeta: MUIDataTableMeta,
          _updateValue: any,
        ) => {
          const createdAt = tableMeta.rowData[1] ? tableMeta.rowData[1] : '';
          const employeeName = tableMeta.rowData[3];
          const hashArquivo = tableMeta.rowData[4];
          return (
            // eslint-disable-next-line jsx-a11y/anchor-is-valid
            <Link
              component="button"
              onClick={() =>
                handleOpenImagePreviewDialog(
                  employeeName,
                  createdAt,
                  hashArquivo,
                )
              }
              color="primary"
            >
              Visualizar Foto
            </Link>
          );
        },
      },
    },
    {
      name: 'status',
      label: 'Status',
      options: {
        customBodyRender: (value: string, _tableMeta: any, _updateValue: any) =>
          getEnum(Number(value), PREREGISTRATION_STATUS_ENUM)?.label ?? '-',
      },
    },
    {
      name: 'hashArquivo',
      label: 'Ações',
      options: {
        filter: false,
        sort: false,
        setCellHeaderProps: () => ({
          className: classes.centerContent,
        }),
        setCellProps: () => ({
          className: classes.centerContent,
        }),
        customBodyRender: (value: any, tableMeta: any, _updateValue: any) => {
          const status = getEnum(
            Number(tableMeta.rowData[5]),
            PREREGISTRATION_STATUS_ENUM,
          )?.value;
          const buttons = [
            <Button
              className={classes.toggleButtons}
              onClick={() => handleOpenConfirmationDialog('Excluir')}
            >
              Excluir
            </Button>,
          ];

          if (status === PENDENTE.value) {
            buttons.unshift(
              <Button
                className={classes.toggleButtons}
                onClick={() => handleOpenConfirmationDialog('Aprovar')}
              >
                Aprovar
              </Button>,
              <Button
                className={classes.toggleButtons}
                onClick={() => handleOpenConfirmationDialog('Reprovar')}
              >
                Reprovar
              </Button>,
            );
          }

          return (
            <ToogleButtonMenu
              toggleMenu={() => {
                toggleActionsMenu(value);
              }}
              options={buttons}
            />
          );
        },
      },
    },
  ];

  const reasonForm = (
    <FormControl style={{ width: 450 }}>
      <FormLabel>Motivo da reprovação</FormLabel>
      <TextField
        placeholder="Informe o motivo da reprovação ao usuário"
        multiline
        rows={4}
        variant="outlined"
        size="medium"
        value={reason}
        rowsMax={4}
        onChange={event => setReason(event.target.value)}
      />
    </FormControl>
  );

  return (
    <>
      <InformativeCard
        imgAltText="Novos Cadastros"
        imgSrc={cautionEnter}
        cardTitle="Novos cadastros de funcionários"
        cardText="Verifique a lista de novos cadastros de funcionários no sistema."
        handleCardClick={handleCardClick}
      />
      <CustomDialog
        open={openDialog}
        handleClose={() => setOpenDialog(false)}
        handleConfirm={() => setOpenDialog(false)}
        showOneButton
        maxWidth="lg"
        okButtonLabel="Fechar"
        showCloseButton
      >
        <CustomDataTable
          title={
            <Grid container direction="row">
              <Typography variant="h6">Cadastros de funcionários</Typography>
              {loading && (
                <CircularProgress
                  size={20}
                  style={{ marginLeft: 15, position: 'relative', top: 4 }}
                />
              )}
            </Grid>
          }
          denseTable
          data={registers}
          columns={columns}
          options={{ elevation: 0, download: true }}
        />
      </CustomDialog>
      <ImagePreviewDialog
        open={openImagePreviewDialog}
        title={imagePreviewDialogTitle}
        loading={loadingImage}
        image={logDistanceImageSelected}
        handleClose={() => setOpenImagePreviewDialog(false)}
      />
      {openConfirmationDialog && (
        <ConfirmationDialog
          open={openConfirmationDialog}
          title={`${actionType} pré-cadastro?`}
          maxWidth="sm"
          mainText={`Deseja realmente ${actionType} o cadastro de ${registerSelected?.pessoa.nome}?`}
          customContent={actionType === 'Reprovar' && reasonForm}
          disableConfirm={actionType === 'Reprovar' && isEmpty(reason)}
          onConfirm={() => handleDialogConfirmationButton()}
          onCancel={() => setOpenConfirmationDialog(false)}
          handleClose={() => setOpenConfirmationDialog(false)}
        />
      )}
    </>
  );
};

export default EmployeesRegistersCard;
